'use client';

import { useI18n } from '@/contexts/I18nContext';

import { AlertCircle, FileText, Loader2, X } from 'lucide-react';
import type { CannyDocument } from './useCannyDocuments';

interface CannyAttachmentsProps {
  documents: CannyDocument[];
  onRemove: (id: string) => void;
}

const STATUS_KEY: Record<CannyDocument['status'], string> = {
  processing: 'canny.doc.processing',
  ready: 'canny.doc.ready',
  failed: 'canny.doc.failed',
};

/**
 * Чипы приложенных документов. Статус показываем явно: индексация идёт в фоне,
 * и без неё Canny по файлу ничего не найдёт — клиент должен понимать, почему.
 */
export function CannyAttachments({ documents, onRemove }: CannyAttachmentsProps) {
  const { t } = useI18n();
  if (documents.length === 0) return null;

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-wrap gap-2 px-1 pt-2">
      {documents.map((doc) => (
        <span
          key={doc.id}
          title={doc.error ?? undefined}
          className="liquid-glass inline-flex max-w-full items-center gap-2 rounded-full py-1.5 pe-1.5 ps-3 text-xs text-white/75"
        >
          {doc.status === 'processing' ? (
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-white/50" />
          ) : doc.status === 'failed' ? (
            <AlertCircle className="h-3.5 w-3.5 shrink-0 text-coral-100" />
          ) : (
            <FileText className="h-3.5 w-3.5 shrink-0 text-white/50" />
          )}
          <span className="truncate">{doc.name}</span>
          <span className={doc.status === 'failed' ? 'text-coral-100' : 'text-white/35'}>
            {t(STATUS_KEY[doc.status])}
          </span>
          <button
            type="button"
            onClick={() => onRemove(doc.id)}
            aria-label={`${t('canny.doc.remove')}: ${doc.name}`}
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white/40 transition-colors hover:text-white"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
    </div>
  );
}
